import type { HeicDecodeRequest, HeicDecodeResponse } from './heic-decode.worker';

const HEIC_EXTENSIONS = ['.heic', '.heif'];
const HEIC_MIME_TYPES = ['image/heic', 'image/heif'];

export function isHeic(file: File): boolean {
  if (HEIC_MIME_TYPES.includes(file.type)) return true;
  const lower = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

export function isAcceptedImage(file: File): boolean {
  return ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
}

export function isAcceptedReceipt(file: File): boolean {
  return isAcceptedImage(file) || isPdf(file) || isHeic(file);
}

/**
 * Which viewer a stored receipt needs. Replaces the old `receiptKindFromKey`,
 * which read the extension off the R2 key that NEW-153 removes.
 *
 * `contentType` is authoritative when present; it is nullable because
 * backfilled rows derived it from an R2 HEAD that could answer without one, so
 * the file name is the fallback. Anything not a PDF is treated as an image,
 * which is what the key-based version did.
 */
export function receiptKindFromFile(
  file: { contentType: string | null; fileName: string } | null | undefined,
): 'pdf' | 'image' {
  if (!file) return 'image';
  if (file.contentType) return file.contentType === 'application/pdf' ? 'pdf' : 'image';
  return file.fileName.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
}

// ---------------------------------------------------------------------------
// HEIC decode fallback
//
// Only reached when the browser cannot decode a HEIC itself — iOS Safari does,
// via createImageBitmap, and that is the common case. This covers desktop
// Chrome/Firefox and Android Chrome, where someone drags in a photo exported
// from a phone.
//
// Returns an ImageBitmap rather than a File because the caller's next step is a
// canvas downscale that wants a bitmap anyway. Encoding a full-resolution JPEG
// here only to re-decode it for the resize is what the old heic2any wrapper
// did, and it cost a second generation of JPEG loss plus the largest allocation
// of the batch.
//
// Null on any failure, never a throw: the caller uploads the raw HEIC and lets
// the server decode it.
// ---------------------------------------------------------------------------

let worker: Worker | null = null;
let nextRequestId = 1;

/**
 * One worker, reused. Spinning one up per image would pay the libheif wasm
 * compile on every file; a receipt batch is small enough that serialising
 * through a single worker costs nothing and bounds memory to one decode.
 */
function getWorker(): Worker | null {
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./heic-decode.worker.ts', import.meta.url), {
      type: 'module',
    });
    return worker;
  } catch {
    // No worker support, or the bundler could not resolve the module. Callers
    // fall back to uploading the original.
    return null;
  }
}

export async function decodeHeicToBitmap(file: File): Promise<ImageBitmap | null> {
  const active = getWorker();
  if (!active) return null;

  const id = nextRequestId++;

  return new Promise<ImageBitmap | null>((resolve) => {
    const onMessage = (event: MessageEvent<HeicDecodeResponse>) => {
      // One worker serves every caller, so ignore replies to other requests.
      if (event.data.id !== id) return;
      cleanup();
      resolve(event.data.ok ? event.data.bitmap : null);
    };

    // A worker that dies takes its queue with it, so drop the handle and let
    // the next call build a fresh one.
    const onError = () => {
      cleanup();
      worker?.terminate();
      worker = null;
      resolve(null);
    };

    function cleanup() {
      active!.removeEventListener('message', onMessage);
      active!.removeEventListener('error', onError);
    }

    active.addEventListener('message', onMessage);
    active.addEventListener('error', onError);
    active.postMessage({ id, blob: file } satisfies HeicDecodeRequest);
  });
}
